import { BaseClient } from '../base-client';
import type {
  ListNotificationsQuery,
  Notification,
  PreferenceView,
  TemplateDefinition,
  UpdatePreferenceInput,
} from './types';

type One<T> = { data: T };
type Many<T> = { data: T[]; meta: { total: number; limit?: number; offset?: number } };

/**
 * NotificationsClient targets apps/notifications (not apps/api). The web
 * app's runtime config has both base URLs; pass the right one when
 * constructing.
 */
export class NotificationsClient extends BaseClient {
  list(query: ListNotificationsQuery = {}): Promise<Many<Notification>> {
    return this.request('/api/notifications/notifications', {
      query: normalize(query as Record<string, unknown>),
    });
  }

  get(id: string): Promise<One<Notification>> {
    return this.request(`/api/notifications/notifications/${encodeURIComponent(id)}`);
  }

  markRead(id: string): Promise<One<Notification>> {
    return this.request(`/api/notifications/notifications/${encodeURIComponent(id)}/read`, {
      method: 'POST',
      body: {},
    });
  }

  // ---- preferences --------------------------------------------------------

  listPreferences(): Promise<Many<PreferenceView>> {
    return this.request('/api/notifications/preferences');
  }

  getPreference(templateName: string): Promise<One<PreferenceView>> {
    return this.request(
      `/api/notifications/preferences/by-name/${encodeURIComponent(templateName)}`,
    );
  }

  updatePreference(
    templateName: string,
    input: UpdatePreferenceInput,
  ): Promise<One<PreferenceView>> {
    return this.request(
      `/api/notifications/preferences/by-name/${encodeURIComponent(templateName)}`,
      { method: 'PUT', body: input },
    );
  }

  deletePreference(templateName: string): Promise<One<PreferenceView>> {
    return this.request(
      `/api/notifications/preferences/by-name/${encodeURIComponent(templateName)}`,
      { method: 'DELETE', body: {} },
    );
  }

  // ---- templates ----------------------------------------------------------

  listTemplates(): Promise<Many<TemplateDefinition>> {
    return this.request('/api/notifications/templates');
  }
}

function normalize(
  q: Record<string, unknown>,
): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return out;
}
